import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';
import RegionSection from '../components/RegionSection';
import CallCard from '../components/CallCard';
import CallCardMobile from '../components/CallCardMobile';
import ResolveCallModal from '../components/ResolveCallModal';
import { useIsMobile, MobilePage, Icon } from '../../mobile';
import Modal from '../../components/Modal';
import PropertySelector from '../../components/PropertySelector';
import PhotoUploader from '../components/PhotoUploader';

import useScheduledCalls from '../../hooks/useScheduledCalls';

const REGIONS = ['Fort Worth', 'Dallas', 'Denton'];

function formatResolutionTime(minutes) {
  if (minutes === null || minutes === undefined) return '--';
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export default function QueueDashboard() {
  const { user, can, canInModule } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [regionFilter, setRegionFilter] = useState('All');
  const [calls, setCalls] = useState([]);
  const { scheduledCalls, refreshScheduled } = useScheduledCalls();
  const [pendingCalls, setPendingCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showResolve, setShowResolve] = useState(null);
  const [showEdit, setShowEdit] = useState(null);
  const [callTypes, setCallTypes] = useState([]);
  const [callReasons, setCallReasons] = useState([]);
  const [resolutionReasons, setResolutionReasons] = useState([]);
  const [vsfLocations, setVsfLocations] = useState([]);
  const [kpiStats, setKpiStats] = useState(null);

  const loadKpiStats = useCallback(async () => {
    try {
      const data = await api.getPublicStats();
      if (data) setKpiStats(data);
    } catch (err) { console.error(err); }
  }, []);

  const loadCalls = useCallback(async () => {
    try {
      const data = await api.getCalls();
      setCalls(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  const loadPendingCalls = useCallback(async () => {
    if (!canInModule('dispatch', 'create_calls')) return;
    try {
      const data = await api.getPendingCalls();
      setPendingCalls(data);
    } catch (err) { console.error(err); }
  }, [can]);

  useEffect(() => {
    loadCalls();
    loadPendingCalls();
    loadKpiStats();
    const interval = setInterval(loadCalls, 10000);
    const pendingInterval = setInterval(loadPendingCalls, 30000);
    const kpiInterval = setInterval(loadKpiStats, 30000);
    return () => { clearInterval(interval); clearInterval(pendingInterval); clearInterval(kpiInterval); };
  }, [loadCalls, loadPendingCalls, loadKpiStats]);

  useEffect(() => {
    api.getCallTypes().then(setCallTypes).catch(console.error);
    api.getCallReasons().then(setCallReasons).catch(console.error);
    api.getResolutionReasons().then(setResolutionReasons).catch(console.error);
    api.getVsfLocations().then(setVsfLocations).catch(console.error);
  }, []);

  async function handleClaim(id) {
    try { await api.claimCall(id); loadCalls(); }
    catch (err) { alert(err.message); }
  }

  async function handleUnclaim(id) {
    try { await api.unclaimCall(id); loadCalls(); }
    catch (err) { alert(err.message); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this call?')) return;
    try { await api.deleteCall(id); loadCalls(); refreshScheduled(); }
    catch (err) { alert(err.message); }
  }

  async function handleCancelScheduled(id) {
    if (!window.confirm('Cancel this scheduled call? It will be permanently deleted.')) return;
    try { await api.deleteCall(id); refreshScheduled(); }
    catch (err) { alert(err.message); }
  }

  async function handleApprovePending(id) {
    try {
      await api.approveCall(id);
      loadPendingCalls();
      loadCalls();
    } catch (err) { alert(err.message); }
  }

  async function handleRejectPending(id) {
    const reason = window.prompt('Reason for rejection (optional):');
    if (reason === null) return; // cancelled
    try {
      await api.rejectCall(id, reason);
      loadPendingCalls();
    } catch (err) { alert(err.message); }
  }

  const openCalls = calls.filter(c => c.status === 'Open');
  const claimedCalls = calls.filter(c => c.status === 'Claimed');

  const modals = (
    <>
      {showCreate && (
        <CreateCallModal
          callTypes={callTypes} callReasons={callReasons} vsfLocations={vsfLocations} user={user}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); loadCalls(); refreshScheduled(); }}
        />
      )}
      {showResolve && (
        <ResolveCallModal
          call={showResolve} resolutionReasons={resolutionReasons}
          onClose={() => setShowResolve(null)}
          onResolved={() => { setShowResolve(null); loadCalls(); }}
        />
      )}
      {showEdit && (
        <EditCallModal
          call={showEdit} callTypes={callTypes} callReasons={callReasons}
          onClose={() => setShowEdit(null)}
          onUpdated={() => { setShowEdit(null); loadCalls(); refreshScheduled(); }}
        />
      )}
    </>
  );

  // ── Phone: one list, region chips, one action per card ──────────────
  // A driver in the truck wants: what is open near me, claim it, resolve
  // it. No KPI strip, no accordions, no Edit/Delete on the card (those
  // live behind the card's options sheet for people who have them).
  if (isMobile) {
    const visible = regionFilter === 'All' ? calls : calls.filter(c => c.region === regionFilter);
    const mine = visible.filter(c => c.status === 'Claimed' && c.claimed_by === user.id);
    const others = visible.filter(c => !(c.status === 'Claimed' && c.claimed_by === user.id));
    const counts = Object.fromEntries(REGIONS.map(r => [r, calls.filter(c => c.region === r).length]));
    const subtitle = `${openCalls.length} open · ${claimedCalls.length} claimed`;
    const side = (
      <div className="m-title-actions">
        <button type="button" className="m-btn-icon sm" aria-label="History" onClick={() => navigate('/history')}>
          <Icon name="clock" size={20} />
        </button>
        {canInModule('dispatch', 'create_calls') && (
          <button type="button" className="m-btn-icon sm" aria-label="New call" onClick={() => setShowCreate(true)}
            style={{ background: 'linear-gradient(135deg, var(--brand-gold) 0%, var(--brand-gold-hover) 100%)', color: '#fff', border: 0 }}>
            <Icon name="plus" size={22} color="#fff" />
          </button>
        )}
      </div>
    );
    const card = call => (
      <CallCardMobile
        key={call.id} call={call} user={user} can={can} canInModule={canInModule}
        onClaim={handleClaim} onUnclaim={handleUnclaim}
        onResolve={c => setShowResolve(c)} onEdit={c => setShowEdit(c)} onDelete={handleDelete}
        onClick={() => navigate(`/calls/${call.id}`)}
      />
    );
    return (
      <MobilePage title="Queue" subtitle={subtitle} side={side}>
        <div className="m-chips">
          <button type="button" className={`m-chip ${regionFilter === 'All' ? 'active' : ''}`} onClick={() => setRegionFilter('All')}>
            All <span className="m-chip-count">{calls.length}</span>
          </button>
          {REGIONS.map(r => (
            <button key={r} type="button" className={`m-chip ${regionFilter === r ? 'active' : ''}`} onClick={() => setRegionFilter(r)}>
              {r} <span className="m-chip-count">{counts[r]}</span>
            </button>
          ))}
        </div>

        {pendingCalls.length > 0 && canInModule('dispatch', 'create_calls') && (
          <>
            <div className="m-section">Pending approval · {pendingCalls.length}</div>
            <div className="m-list" style={{ marginBottom: 12 }}>
              {pendingCalls.map(call => (
                <PendingCallCard key={call.id} call={call}
                  onApprove={() => handleApprovePending(call.id)} onReject={() => handleRejectPending(call.id)} />
              ))}
            </div>
          </>
        )}

        {scheduledCalls.length > 0 && (
          <>
            <div className="m-section">Scheduled · {scheduledCalls.length}</div>
            <div className="m-list" style={{ marginBottom: 12 }}>
              {scheduledCalls.map(call => (
                <ScheduledCallCard key={call.id} call={call} onCancel={() => handleCancelScheduled(call.id)} />
              ))}
            </div>
          </>
        )}

        {loading ? (
          <div className="loading">Loading...</div>
        ) : visible.length === 0 ? (
          <div className="m-card" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 28 }}>
            No active calls{regionFilter !== 'All' ? ` in ${regionFilter}` : ''}.
          </div>
        ) : (
          <>
            {mine.length > 0 && (
              <>
                <div className="m-section">My calls · {mine.length}</div>
                <div className="m-list" style={{ marginBottom: 12 }}>{mine.map(card)}</div>
              </>
            )}
            {others.length > 0 && (
              <>
                {mine.length > 0 && <div className="m-section">Everything else · {others.length}</div>}
                <div className="m-list">{others.map(card)}</div>
              </>
            )}
          </>
        )}
        {modals}
      </MobilePage>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h2>Space Call Queue</h2>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {canInModule('dispatch', 'create_calls') && (
            <button className="btn btn-gold" onClick={() => setShowCreate(true)}>+ New Call</button>
          )}
          <button className="btn btn-primary" onClick={() => navigate('/history')}>History</button>
        </div>
      </div>

      <div className="kpi-grid">
        <div className="kpi-card" style={{ '--kpi-accent': '#3b82f6' }}>
          <div className="kpi-value">{kpiStats?.total_calls ?? (openCalls.length + claimedCalls.length)}</div>
          <div className="kpi-label">Total Calls</div>
        </div>
        <div className="kpi-card" style={{ '--kpi-accent': '#f59e0b' }}>
          <div className="kpi-value">{claimedCalls.length}</div>
          <div className="kpi-label">Claimed</div>
        </div>
        <div className="kpi-card" style={{ '--kpi-accent': '#64748b' }}>
          <div className="kpi-value">{formatResolutionTime(kpiStats?.avg_resolution_minutes)}</div>
          <div className="kpi-label">Avg Resolution</div>
        </div>
      </div>

      {pendingCalls.length > 0 && canInModule('dispatch', 'create_calls') && (
        <div className="pending-section">
          <div className="pending-header">
            <h3>Pending Submissions <span className="pending-count-badge">{pendingCalls.length}</span></h3>
            <span className="pending-subtitle">Online submissions awaiting approval</span>
          </div>
          <div className="calls-grid">
            {pendingCalls.map(call => (
              <PendingCallCard
                key={call.id}
                call={call}
                onApprove={() => handleApprovePending(call.id)}
                onReject={() => handleRejectPending(call.id)}
              />
            ))}
          </div>
        </div>
      )}

      {scheduledCalls.length > 0 && (
        <div className="scheduled-section">
          <div className="scheduled-header">
            <h3>Scheduled Calls <span className="scheduled-count-badge">{scheduledCalls.length}</span></h3>
          </div>
          <div className="calls-grid">
            {scheduledCalls.map(call => (
              <ScheduledCallCard
                key={call.id}
                call={call}
                onCancel={() => handleCancelScheduled(call.id)}
              />
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        REGIONS.map(region => {
          const regionCalls = calls.filter(c => c.region === region);
          return (
            <RegionSection key={region} region={region} count={regionCalls.length}>
              {regionCalls.length === 0 ? (
                <div className="empty-region">No active calls</div>
              ) : (
                <div className="calls-grid">
                  {regionCalls.map(call => (
                    <CallCard
                      key={call.id} call={call} user={user} can={can} canInModule={canInModule}
                      onClaim={handleClaim}
                      onUnclaim={handleUnclaim}
                      onResolve={c => setShowResolve(c)}
                      onEdit={c => setShowEdit(c)}
                      onDelete={handleDelete}
                      onClick={() => navigate(`/calls/${call.id}`)}
                    />
                  ))}
                </div>
              )}
            </RegionSection>
          );
        })
      )}

      {modals}
    </div>
  );
}

// ===== CREATE CALL MODAL =====
function CreateCallModal({ callTypes, callReasons, vsfLocations = [], user, onClose, onCreated }) {
  const [isEmergency, setIsEmergency] = useState(false);
  const [region, setRegion] = useState('Fort Worth');
  const [property, setProperty] = useState(null);
  const [customAddress, setCustomAddress] = useState('');
  const [pickupProperty, setPickupProperty] = useState(null);
  const [pickupCustomAddress, setPickupCustomAddress] = useState('');
  const [dropoffProperty, setDropoffProperty] = useState(null);
  const [dropoffCustomAddress, setDropoffCustomAddress] = useState('');
  // Internal-only: which property this courtesy tow is attributed to for
  // profitability tracking. Not shown to drivers; not part of pickup/dropoff.
  const [sourceProperty, setSourceProperty] = useState(null);
  const [vsfId, setVsfId] = useState('');
  const [towedFromProperty, setTowedFromProperty] = useState(null);
  const [takebackDropMode, setTakebackDropMode] = useState('property');
  const [takebackCustomAddress, setTakebackCustomAddress] = useState('');
  const [takebackNotes, setTakebackNotes] = useState('');
  const [salesRepNotifyId, setSalesRepNotifyId] = useState('');
  const [notifiableUsers, setNotifiableUsers] = useState([]);
  const [callTypeId, setCallTypeId] = useState('');
  const [callReasonId, setCallReasonId] = useState('');
  const [vehicleInfo, setVehicleInfo] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [spaceNumber, setSpaceNumber] = useState('');
  const [notes, setNotes] = useState('');

  // Scheduling state
  const [scheduleMode, setScheduleMode] = useState('now'); // 'now' | 'later'
  const [scheduledDate, setScheduledDate] = useState('');   // YYYY-MM-DD
  const [scheduledTime, setScheduledTime] = useState('');   // HH:MM

  // Filter call types by allowed_roles (maps team → legacy role for backward compat)
  const TEAM_ROLE = { 'Driver Operations': 'driver', 'Dispatch': 'dispatcher', 'Sales': 'sales', 'Truck Maintenance': 'maintenance' };
  const effectiveRole = TEAM_ROLE[user?.team_name] || user?.role;
  const allowedCallTypes = callTypes.filter(t => {
    if (!t.allowed_roles) return true;
    // Admin+ can use all call types
    if (['admin', 'super_admin'].includes(user?.permission_tier)) return true;
    return t.allowed_roles.split(',').includes(effectiveRole);
  });

  // Derive mode from selected call type
  const selectedType = callTypes.find(t => String(t.id) === String(callTypeId));
  const mode = selectedType?.mode || 'standard';
  const selectedTypeName = selectedType?.name || '';
  const selectedVsf = vsfLocations.find(v => String(v.id) === String(vsfId));

  const [photos, setPhotos] = useState([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const isScheduling = scheduleMode === 'later';
  const isCustomLocation = mode === 'location' && property && property.id === 'custom';
  const isPickupCustom = pickupProperty && pickupProperty.id === 'custom';
  const isDropoffCustom = dropoffProperty && dropoffProperty.id === 'custom';

  // Load notifiable users for Sales Rep dropdown (Take Back)
  useEffect(() => {
    api.getNotifiableUsers().then(setNotifiableUsers).catch(console.error);
  }, []);

  // Reset location/scheduling fields when call type changes
  function handleCallTypeChange(newTypeId) {
    setCallTypeId(newTypeId);
    const newType = callTypes.find(t => String(t.id) === String(newTypeId));
    const newMode = newType?.mode || 'standard';
    if (newMode === 'standard') {
      setScheduleMode('now');
      setScheduledDate('');
      setScheduledTime('');
    }
    setProperty(null);
    setCustomAddress('');
    setPickupProperty(null);
    setPickupCustomAddress('');
    setDropoffProperty(null);
    setDropoffCustomAddress('');
    setSourceProperty(null);
    setVsfId('');
    setTowedFromProperty(null);
    setTakebackDropMode('property');
    setTakebackCustomAddress('');
    setTakebackNotes('');
    setSalesRepNotifyId('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    const fd = new FormData();
    // Region: takeback uses destination property region (handled in takeback block below)
    // courtesy/custom location use manual region selector
    // standard/location use property region or fallback
    if (mode !== 'takeback') {
      fd.append('region', (mode === 'courtesy' || isCustomLocation) ? region : (property?.region || region));
    }
    fd.append('call_type_id', callTypeId);
    fd.append('call_reason_id', callReasonId);
    fd.append('vehicle_info', vehicleInfo);
    fd.append('contact_name', contactName);
    fd.append('contact_number', contactNumber);
    fd.append('space_number', spaceNumber);
    // For take backs, prepend take-back notes to regular notes
    if (mode === 'takeback' && takebackNotes.trim()) {
      fd.append('notes', `TAKE BACK: ${takebackNotes}${notes ? '\n' + notes : ''}`);
    } else {
      fd.append('notes', notes);
    }
    fd.append('is_emergency', isEmergency ? '1' : '0');

    // Schedule for later — send due_time, server computes notification time
    if (scheduleMode === 'later') {
      if (!scheduledDate || !scheduledTime) {
        setError('Please select both a due date and time');
        setSubmitting(false);
        return;
      }
      const dueDateTime = new Date(`${scheduledDate}T${scheduledTime}:00`);
      if (dueDateTime.getTime() <= Date.now()) {
        setError('Due time must be in the future');
        setSubmitting(false);
        return;
      }
      fd.append('due_time', dueDateTime.toISOString());
    }

    if (mode === 'courtesy') {
      // Resolve pickup location from property or custom address
      let finalPickup = '';
      if (pickupProperty && pickupProperty.id !== 'custom') {
        finalPickup = pickupProperty.name + ' — ' + pickupProperty.address;
      } else if (isPickupCustom) {
        if (!pickupCustomAddress.trim()) {
          setError('Please enter a pickup address');
          setSubmitting(false);
          return;
        }
        finalPickup = pickupCustomAddress;
      }

      // Resolve dropoff location from property or custom address
      let finalDropoff = '';
      if (dropoffProperty && dropoffProperty.id !== 'custom') {
        finalDropoff = dropoffProperty.name + ' — ' + dropoffProperty.address;
      } else if (isDropoffCustom) {
        if (!dropoffCustomAddress.trim()) {
          setError('Please enter a drop-off address');
          setSubmitting(false);
          return;
        }
        finalDropoff = dropoffCustomAddress;
      }

      fd.append('location', finalPickup);
      fd.append('pickup_location', finalPickup);
      fd.append('dropoff_location', finalDropoff);

      // Internal tracking: dispatcher must explicitly attribute the courtesy
      // tow to a property from the Apollo property list. Pickup/dropoff are
      // NOT used for attribution — they may be off the side of the road or
      // a different property entirely. This field is required and is never
      // shown on the call card visible to drivers.
      if (!sourceProperty || !sourceProperty.id || sourceProperty.id === 'custom') {
        setError('Please select the Property Associated With (internal tracking) for this courtesy tow');
        setSubmitting(false);
        return;
      }
      fd.append('source_property_id', sourceProperty.id);
    } else if (mode === 'location') {
      if (property && property.id !== 'custom') {
        fd.append('property_id', property.id);
        fd.append('location', property.address);
      } else if (isCustomLocation) {
        if (!customAddress.trim()) {
          setError('Please enter an address for the custom location');
          setSubmitting(false);
          return;
        }
        fd.append('location', customAddress);
        fd.append('property_name', selectedTypeName || 'Custom Location');
      } else {
        setError('Please select a property or Custom Location');
        setSubmitting(false);
        return;
      }
    } else if (mode === 'takeback') {
      // VSF = pickup location
      if (!vsfId) {
        setError('Please select a VSF pickup location');
        setSubmitting(false);
        return;
      }
      if (!towedFromProperty) {
        setError('Please select the property towed from');
        setSubmitting(false);
        return;
      }
      if (!takebackNotes.trim()) {
        setError('Take Back Notes are required');
        setSubmitting(false);
        return;
      }
      if (takebackDropMode === 'custom' && !takebackCustomAddress.trim()) {
        setError('Please enter a custom drop-off address');
        setSubmitting(false);
        return;
      }
      if (!salesRepNotifyId) {
        setError('Please select a Sales Rep to Notify');
        setSubmitting(false);
        return;
      }

      const vsf = vsfLocations.find(v => String(v.id) === String(vsfId));
      fd.append('pickup_location', vsf ? `${vsf.name} — ${vsf.address}` : '');
      fd.append('vsf_location_id', vsfId);

      // Region ALWAYS from "Property Towed From"
      fd.append('region', towedFromProperty.region);
      fd.append('property_id', towedFromProperty.id);
      fd.append('property_name', towedFromProperty.name);

      // Drop-off location
      if (takebackDropMode === 'property') {
        fd.append('location', towedFromProperty.address);
        fd.append('dropoff_location', `${towedFromProperty.name} — ${towedFromProperty.address}`);
      } else {
        fd.append('location', takebackCustomAddress);
        fd.append('dropoff_location', takebackCustomAddress);
      }

      fd.append('sales_rep_notify_id', salesRepNotifyId);
    } else if (property) {
      fd.append('property_id', property.id);
      fd.append('location', property.address);
    }

    for (const file of photos) {
      fd.append('photos', file);
    }

    try {
      await api.createCall(fd);
      onCreated();
    } catch (err) { setError(err.message); }
    setSubmitting(false);
  }

  return (
    <Modal title="New Service Call" onClose={onClose}>
      {error && <div className="alert alert-error">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className={`emergency-toggle-label${isEmergency ? ' active' : ''}`}>
            <input type="checkbox" checked={isEmergency} onChange={e => setIsEmergency(e.target.checked)} />
            <span className="emergency-toggle-text">🚨 Emergency / 911</span>
          </label>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Call Type</label>
            <select value={callTypeId} onChange={e => handleCallTypeChange(e.target.value)} required>
              <option value="">Select...</option>
              {allowedCallTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          {mode !== 'takeback' && (
            <div className="form-group">
              <label>Call Reason</label>
              <select value={callReasonId} onChange={e => setCallReasonId(e.target.value)} required>
                <option value="">Select...</option>
                {callReasons.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          )}
        </div>

        {mode === 'courtesy' && (
          <>
            <div
              className="form-group"
              style={{
                background: '#FFF8E1',
                border: '2px solid #F5C518',
                borderRadius: 8,
                padding: '14px 16px',
                marginBottom: 16,
              }}
            >
              <label style={{ fontWeight: 700, fontSize: 15, color: '#5C4A00' }}>
                Property Associated With <span style={{ color: '#C0392B' }}>*</span>
              </label>
              <div style={{ fontSize: 12, color: '#7A6500', marginBottom: 8 }}>
                Internal tracking only — used for property profitability. NOT shown to drivers. Pickup/drop-off are tracked separately and may differ from this property.
              </div>
              <PropertySelector value={sourceProperty} onChange={setSourceProperty} />
              {sourceProperty && sourceProperty.id !== 'custom' && (
                <div className="text-muted" style={{ marginTop: 4 }}>
                  {sourceProperty.address}, {sourceProperty.city} &mdash; {sourceProperty.region}
                </div>
              )}
            </div>
            <div className="form-group">
              <label>Region</label>
              <select value={region} onChange={e => setRegion(e.target.value)}>
                {['Fort Worth', 'Dallas', 'Denton'].map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Pickup Location</label>
              <PropertySelector value={pickupProperty} onChange={setPickupProperty} showCustomLocation />
              {pickupProperty && pickupProperty.id !== 'custom' && (
                <div className="text-muted" style={{marginTop:4}}>{pickupProperty.address}, {pickupProperty.city} &mdash; {pickupProperty.region}</div>
              )}
            </div>
            {isPickupCustom && (
              <div className="form-group">
                <label>Pickup Address</label>
                <input value={pickupCustomAddress} onChange={e => setPickupCustomAddress(e.target.value)} placeholder="Enter pickup address" />
              </div>
            )}

            <div className="form-group">
              <label>Drop-off Location</label>
              <PropertySelector value={dropoffProperty} onChange={setDropoffProperty} showCustomLocation />
              {dropoffProperty && dropoffProperty.id !== 'custom' && (
                <div className="text-muted" style={{marginTop:4}}>{dropoffProperty.address}, {dropoffProperty.city} &mdash; {dropoffProperty.region}</div>
              )}
            </div>
            {isDropoffCustom && (
              <div className="form-group">
                <label>Drop-off Address</label>
                <input value={dropoffCustomAddress} onChange={e => setDropoffCustomAddress(e.target.value)} placeholder="Enter drop-off address" />
              </div>
            )}
          </>
        )}

        {mode === 'location' && (
          <>
            <div className="form-group">
              <label>Property / Location</label>
              <PropertySelector value={property} onChange={setProperty} showCustomLocation />
              {property && property.id !== 'custom' && (
                <div className="text-muted" style={{marginTop:4}}>{property.address}, {property.city} &mdash; {property.region}</div>
              )}
            </div>

            {isCustomLocation && (
              <>
                <div className="form-group">
                  <label>Address</label>
                  <input value={customAddress} onChange={e => setCustomAddress(e.target.value)} placeholder="Enter address" />
                </div>
                <div className="form-group">
                  <label>Region</label>
                  <select value={region} onChange={e => setRegion(e.target.value)}>
                    {['Fort Worth', 'Dallas', 'Denton'].map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
              </>
            )}
          </>
        )}

        {mode === 'takeback' && (
          <>
            <div className="form-group">
              <label>VSF (Pickup Location)</label>
              <select value={vsfId} onChange={e => setVsfId(e.target.value)} required>
                <option value="">Select VSF...</option>
                {vsfLocations.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
              {selectedVsf && <div className="text-muted" style={{marginTop:4}}>{selectedVsf.address}, {selectedVsf.city}</div>}
            </div>
            <div className="form-group">
              <label>Property Towed From</label>
              <PropertySelector value={towedFromProperty} onChange={setTowedFromProperty} />
              {towedFromProperty && (
                <div className="text-muted" style={{marginTop:4}}>{towedFromProperty.address}, {towedFromProperty.city} &mdash; <strong>{towedFromProperty.region}</strong></div>
              )}
            </div>
            <div className="form-group">
              <label>Drop Off Location</label>
              <div className="radio-group schedule-radio-group">
                <label className={takebackDropMode === 'property' ? 'selected' : ''}>
                  <input type="radio" value="property" checked={takebackDropMode === 'property'} onChange={() => setTakebackDropMode('property')} />
                  Property Towed From
                </label>
                <label className={takebackDropMode === 'custom' ? 'selected' : ''}>
                  <input type="radio" value="custom" checked={takebackDropMode === 'custom'} onChange={() => setTakebackDropMode('custom')} />
                  Custom Location
                </label>
              </div>
            </div>
            {takebackDropMode === 'custom' && (
              <div className="form-group">
                <label>Drop-off Address</label>
                <input value={takebackCustomAddress} onChange={e => setTakebackCustomAddress(e.target.value)} placeholder="Enter drop-off address" />
              </div>
            )}
            <div className="form-group">
              <label>Take Back Notes <span style={{color:'var(--danger)'}}>*</span></label>
              <textarea value={takebackNotes} onChange={e => setTakebackNotes(e.target.value)}
                placeholder="Where should the vehicle be placed? e.g., Front lot near office, Space #12, Back row by dumpster"
                rows={3} required />
            </div>
            <div className="form-group">
              <label>Sales Rep to Notify <span style={{color:'var(--danger)'}}>*</span></label>
              <select value={salesRepNotifyId} onChange={e => setSalesRepNotifyId(e.target.value)} required>
                <option value="">Select sales rep...</option>
                {notifiableUsers.map(u => (
                  <option key={u.id} value={u.id}>{u.full_name}</option>
                ))}
              </select>
              <small className="text-muted">This person will receive an SMS when the call is resolved.</small>
            </div>
          </>
        )}

        {mode === 'standard' && (
          <div className="form-group">
            <label>Property</label>
            <PropertySelector value={property} onChange={setProperty} />
            {property && <div className="text-muted" style={{marginTop:4}}>{property.address}, {property.city} &mdash; {property.region}</div>}
          </div>
        )}

        <div className="form-group">
          <label>When to Send</label>
          <div className="radio-group schedule-radio-group">
            <label className={scheduleMode === 'now' ? 'selected' : ''}>
              <input type="radio" value="now" checked={scheduleMode === 'now'} onChange={() => setScheduleMode('now')} />
              Send Now
            </label>
            <label className={scheduleMode === 'later' ? 'selected' : ''}>
              <input type="radio" value="later" checked={scheduleMode === 'later'} onChange={() => setScheduleMode('later')} />
              Schedule for Later
            </label>
          </div>
        </div>

        {scheduleMode === 'later' && (
          <>
            <div className="form-row schedule-datetime-row">
              <div className="form-group">
                <label>Due Date</label>
                <input
                  type="date"
                  value={scheduledDate}
                  onChange={e => setScheduledDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>
              <div className="form-group">
                <label>Due Time <span className="text-muted">(Central)</span></label>
                <input
                  type="time"
                  value={scheduledTime}
                  onChange={e => setScheduledTime(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="text-muted" style={{fontSize: 13, marginTop: -8}}>Drivers will be notified 60 minutes before the due time</div>
          </>
        )}

        {selectedTypeName === 'Space Call' && (
          <div className="form-group">
            <label>Space Number</label>
            <input value={spaceNumber} onChange={e => setSpaceNumber(e.target.value)} placeholder="e.g. 101, A-5, etc." />
          </div>
        )}

        <div className="form-group">
          <label>Vehicle Info</label>
          <input value={vehicleInfo} onChange={e => setVehicleInfo(e.target.value)} placeholder="Year, Make, Model, Color, Plate" />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Contact Name <span className="text-muted">(optional)</span></label>
            <input value={contactName} onChange={e => setContactName(e.target.value)} placeholder="Caller or property manager name" />
          </div>
          <div className="form-group">
            <label>Contact Number <span className="text-muted">(optional)</span></label>
            <input value={contactNumber} onChange={e => setContactNumber(e.target.value)} placeholder="(555) 123-4567" />
          </div>
        </div>

        <div className="form-group">
          <label>Notes</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} />
        </div>

        <div className="form-group">
          <label>Photos</label>
          <PhotoUploader files={photos} onChange={setPhotos} />
        </div>

        <div className="modal-actions">
          <button type="button" className="btn btn-muted" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting
              ? (isScheduling ? 'Scheduling...' : 'Creating...')
              : (isScheduling ? 'Schedule Call' : 'Create Call')
            }
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ===== SCHEDULED CALL CARD =====
function ScheduledCallCard({ call, onCancel }) {
  const now = new Date();

  // If call has due_time, show countdown to due time; otherwise fall back to scheduled_for
  const hasDueTime = !!call.due_time;
  const dueTime = hasDueTime ? new Date(call.due_time.replace(' ', 'T') + 'Z') : null;
  const notifyTime = new Date(call.scheduled_for.replace(' ', 'T') + 'Z');
  const countdownTarget = dueTime || notifyTime;

  const diffMs = countdownTarget - now;
  const diffMin = Math.max(0, Math.floor(diffMs / 60000));
  const hours = Math.floor(diffMin / 60);
  const mins = diffMin % 60;

  let countdown;
  if (diffMs <= 0) {
    countdown = hasDueTime ? 'Due now' : 'Activating soon...';
  } else if (hours > 0) {
    countdown = `${hasDueTime ? 'Due in ' : ''}${hours}h ${mins}m`;
  } else {
    countdown = `${hasDueTime ? 'Due in ' : ''}${mins}m`;
  }

  // Format the due/scheduled time for display
  const displayTarget = dueTime || notifyTime;
  const displayDate = displayTarget.toLocaleDateString('en-US', {
    timeZone: 'America/Chicago', month: 'short', day: 'numeric'
  });
  const displayTime = displayTarget.toLocaleTimeString('en-US', {
    timeZone: 'America/Chicago', hour: 'numeric', minute: '2-digit'
  });

  // Format notification time for heads-up info
  const notifyDisplay = hasDueTime ? notifyTime.toLocaleTimeString('en-US', {
    timeZone: 'America/Chicago', hour: 'numeric', minute: '2-digit'
  }) : null;
  const notifyDiffMs = notifyTime - now;

  return (
    <div className="call-card status-scheduled">
      <div className="call-card-header">
        <div className="call-card-title">
          <span className="scheduled-badge">Scheduled</span>
        </div>
        <span className="scheduled-countdown">{countdown}</span>
      </div>
      <div className="call-card-body">
        <div className="call-detail"><strong>Location:</strong> {call.property_name || call.location}</div>
        {call.pickup_location && <div className="call-detail"><strong>Pickup:</strong> {call.pickup_location}</div>}
        {call.dropoff_location && <div className="call-detail"><strong>Drop-off:</strong> {call.dropoff_location}</div>}
        <div className="call-detail scheduled-time">
          <strong>{hasDueTime ? 'Due:' : 'Scheduled:'}</strong> {displayDate} at {displayTime} CT
        </div>
        {hasDueTime && notifyDisplay && (
          <div className="call-detail" style={{fontSize: 13, color: 'var(--text-muted)'}}>
            {notifyDiffMs > 0
              ? `Drivers notified at ${notifyDisplay} CT`
              : `Drivers notified at ${notifyDisplay} CT`
            }
          </div>
        )}
        {call.vehicle_info && <div className="call-detail"><strong>Vehicle:</strong> {call.vehicle_info}</div>}
        {call.notes && <div className="call-detail"><strong>Notes:</strong> {call.notes}</div>}
      </div>
      <div className="call-card-actions">
        <button className="btn btn-danger btn-sm" onClick={onCancel}>Cancel Call</button>
      </div>
    </div>
  );
}

// ===== PENDING CALL CARD =====
function PendingCallCard({ call, onApprove, onReject }) {
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  // Calculate time since submission
  const createdAt = new Date(call.created_at.replace(' ', 'T') + 'Z');
  const now = new Date();
  const diffMs = now - createdAt;
  const diffMin = Math.floor(diffMs / 60000);
  let timeAgo;
  if (diffMin < 1) timeAgo = 'Just now';
  else if (diffMin < 60) timeAgo = `${diffMin}m ago`;
  else {
    const hours = Math.floor(diffMin / 60);
    if (hours < 24) timeAgo = `${hours}h ${diffMin % 60}m ago`;
    else timeAgo = `${Math.floor(hours / 24)}d ago`;
  }

  // Photo URLs from attached photos
  const photoUrls = (call.photos || []).map(p => `/api/uploads/${p.file_path}`);

  async function handleApprove() {
    setApproving(true);
    await onApprove();
    setApproving(false);
  }

  async function handleReject() {
    setRejecting(true);
    await onReject();
    setRejecting(false);
  }

  return (
    <div className="call-card status-pending">
      <div className="call-card-header">
        <div className="call-card-title">
          <span className="pending-badge">Pending</span>
          <span className="pending-region-badge">{call.region}</span>
        </div>
        <span className="pending-time-ago">{timeAgo}</span>
      </div>
      <div className="call-card-body">
        <div className="call-detail"><strong>Property:</strong> {call.property_name}</div>
        {call.vehicle_info && <div className="call-detail"><strong>Vehicle:</strong> {call.vehicle_info}</div>}
        {call.space_number && <div className="call-detail"><strong>Space:</strong> #{call.space_number}</div>}
        {call.contact_name && <div className="call-detail"><strong>Contact:</strong> {call.contact_name}{call.contact_number ? ` (${call.contact_number})` : ''}</div>}
        {call.notes && <div className="call-detail"><strong>Notes:</strong> {call.notes}</div>}
        {photoUrls.length > 0 && (
          <div className="pending-photos">
            {photoUrls.map((url, i) => (
              <img key={i} src={url} alt={`Photo ${i + 1}`} className="pending-photo-thumb" onClick={() => window.open(url, '_blank')} />
            ))}
          </div>
        )}
      </div>
      <div className="call-card-actions pending-actions">
        <button className="btn btn-success btn-sm" onClick={handleApprove} disabled={approving}>
          {approving ? 'Approving...' : 'Approve'}
        </button>
        <button className="btn btn-danger btn-sm" onClick={handleReject} disabled={rejecting}>
          {rejecting ? 'Rejecting...' : 'Reject'}
        </button>
      </div>
    </div>
  );
}

// ===== EDIT CALL MODAL =====
function EditCallModal({ call, callTypes, callReasons, onClose, onUpdated }) {
  const [form, setForm] = useState({
    region: call.region || 'Fort Worth',
    call_type_id: call.call_type_id || '',
    call_reason_id: call.call_reason_id || '',
    vehicle_info: call.vehicle_info || '',
    contact_name: call.contact_name || '',
    contact_number: call.contact_number || '',
    space_number: call.space_number || '',
    notes: call.notes || '',
    location: call.location || '',
    is_emergency: call.is_emergency || 0,
  });
  const [error, setError] = useState('');

  const editTypeName = callTypes.find(t => String(t.id) === String(form.call_type_id))?.name || '';
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try { await api.updateCall(call.id, form); onUpdated(); }
    catch (err) { setError(err.message); }
    setSubmitting(false);
  }

  return (
    <Modal title={`Edit Call #${call.id}`} onClose={onClose}>
      {error && <div className="alert alert-error">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className={`emergency-toggle-label${form.is_emergency ? ' active' : ''}`}>
            <input type="checkbox" checked={!!form.is_emergency} onChange={e => setForm({...form, is_emergency: e.target.checked ? 1 : 0})} />
            <span className="emergency-toggle-text">🚨 Emergency / 911</span>
          </label>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Region</label>
            <select value={form.region} onChange={e => setForm({...form, region: e.target.value})}>
              {['Fort Worth','Dallas','Denton'].map(r => <option key={r}>{r}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Location</label>
            <input value={form.location} onChange={e => setForm({...form, location: e.target.value})} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Call Type</label>
            <select value={form.call_type_id} onChange={e => setForm({...form, call_type_id: e.target.value})}>
              {callTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Call Reason</label>
            <select value={form.call_reason_id} onChange={e => setForm({...form, call_reason_id: e.target.value})}>
              {callReasons.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
        </div>
        {editTypeName === 'Space Call' && (
          <div className="form-group">
            <label>Space Number</label>
            <input value={form.space_number} onChange={e => setForm({...form, space_number: e.target.value})} placeholder="e.g. 101, A-5, etc." />
          </div>
        )}
        <div className="form-group">
          <label>Vehicle Info</label>
          <input value={form.vehicle_info} onChange={e => setForm({...form, vehicle_info: e.target.value})} />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Contact Name <span className="text-muted">(optional)</span></label>
            <input value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} />
          </div>
          <div className="form-group">
            <label>Contact Number <span className="text-muted">(optional)</span></label>
            <input value={form.contact_number} onChange={e => setForm({...form, contact_number: e.target.value})} />
          </div>
        </div>
        <div className="form-group">
          <label>Notes</label>
          <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} />
        </div>
        <div className="modal-actions">
          <button type="button" className="btn btn-muted" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
